import { createFileRoute } from "@tanstack/react-router";
import { exteriorPaletteLibraryAsync, getExteriorPalette } from "building-generation/utils/builtinPaletteLoader";
import type { ExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { downloadBlob } from "common/utils/downloadFile";
import { BuiltinHomeSettingsId } from "doc/settings/builtinHomeSettingsId";
import { initTHREEJSRenderer } from "gfx/initPhysicallyBasedRenderer";
import { initWebGLContext } from "gfx/initWebGLContext";
import { BakeColorTextureMode } from "gfx/materials/designMaterialRuntimeConfig";
import { THREEViewerServices } from "gfx/THREEViewerServices";
import JSZip from "jszip";
import {
  genExteriorPaletteThumbnail,
  genExteriorPaletteThumbnailBlob,
  preloadPaletteThumbnailElements,
} from "lib/house/paletteThumbnail";
import { getFileStorageBaseURL } from "lib/storage/fileStorage";
import getPageTitle from "lib/utils/getPageTitle";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import Select from "ui/forms/Select";
import withAuthentication from "../../components/withAuthentication";

enum FilenameStyle {
  ID = "ID",
  PRETTY_NAME = "PRETTY_NAME",
  ID_AND_PRETTY_NAME = "ID_AND_PRETTY_NAME",
}

const filenameIllegalCharsReplacementOptions = [
  { label: "underscore", value: "_" },
  { label: "dash", value: "-" },
  { label: "space", value: " " },
];

function PaletteThumbnailTest() {
  const thumbnailImgRef = useRef<HTMLImageElement | null>(null);
  const services = useRef<THREEViewerServices | undefined>(undefined);
  const [filter, setFilter] = useState<string>("");
  const [currentPaletteKey, setCurrentPaletteKey] = useState<string>(BuiltinHomeSettingsId.SmithDouglas_B1A);
  const [fileFormat, setFileFormat] = useState<"image/png" | "image/jpeg">("image/jpeg");
  const [allowMaterialThumbnailCache, setAllowMaterialThumbnailCache] = useState(false);
  const [paletteLayout, setPaletteLayout] = useState<"rect" | "square">("square");
  const [filenameStyle, setFilenameStyle] = useState(FilenameStyle.ID_AND_PRETTY_NAME);
  const [filenameIllegalCharsReplacement, setFilenameIllegalCharsReplacement] = useState("_");
  const [paletteFileNameExample, setPaletteFileNameExample] = useState("");
  const [forceFlatColorFallback, setForceFlatColorFallback] = useState(false);
  const [fileStorageBaseURL, setFileStorageBaseURL] = useState<string | null>(null);

  useEffect(() => {
    void getFileStorageBaseURL().then((url) => {
      if (url) {
        setFileStorageBaseURL(url);
      }
    });
  }, []);

  const [filteredPaletteKeys, setFilteredPaletteKeys] = useState<string[]>(exteriorPaletteLibraryAsync.ids);

  function paletteFileName(paletteKey: string, palette: ExteriorPalette): string {
    let basename = paletteKey;
    if (filenameStyle === FilenameStyle.PRETTY_NAME) {
      basename = palette.name;
    } else if (filenameStyle === FilenameStyle.ID_AND_PRETTY_NAME) {
      basename = paletteKey + "_" + palette.name;
    }
    basename = basename.replace(/[\/\\:\*\?"<>\|]/g, filenameIllegalCharsReplacement);
    return basename + (fileFormat === "image/png" ? ".png" : ".jpg");
  }

  async function downloadCurrent() {
    const paletteKey = currentPaletteKey; // So that the value won't change while we're running async code
    const palette = await getExteriorPalette(paletteKey);
    if (!palette || !services.current || !fileStorageBaseURL) {
      return;
    }
    const blob = await genExteriorPaletteThumbnailBlob(palette, services.current, fileFormat, {}, fileStorageBaseURL, {
      noCache: !allowMaterialThumbnailCache,
      forceFlatColorFallback,
    });
    downloadBlob(paletteFileName(paletteKey, palette), blob);
  }

  async function downloadAll() {
    if (filteredPaletteKeys.length === 0 || !services.current || !fileStorageBaseURL) {
      return;
    }
    const paletteKeys = filteredPaletteKeys; // So that the value won't change while we're running async code
    const zip = new JSZip();

    for (const paletteKey of paletteKeys) {
      const palette = await getExteriorPalette(paletteKey);
      if (!palette) {
        continue;
      }
      const blob = await genExteriorPaletteThumbnailBlob(
        palette,
        services.current,
        fileFormat,
        {},
        fileStorageBaseURL,
        {
          noCache: !allowMaterialThumbnailCache,
          forceFlatColorFallback,
        },
      );
      zip.file(paletteFileName(paletteKey, palette), blob);
    }

    const zipFile: Blob = (await zip.generateAsync({ type: "blob" })) as Blob;
    downloadBlob("palette-thumbnails.zip", zipFile);
  }

  useEffect(() => {
    const { canvas, glContext, glContextAttributes } = initWebGLContext({
      framebufferOptions: {
        antialias: false,
        depthStencil: true,
      },
      throwOnGLError: false,
      logGLResourceUsage: false,
      label: "PaletteThumbnailTest",
    });

    const renderer = initTHREEJSRenderer(canvas, glContext, glContextAttributes);
    void preloadPaletteThumbnailElements();

    services.current = new THREEViewerServices({
      renderer,
      enableLightOffsetMap: true,
      offsetMapLightCount: 8,
      onScreenRenderingDisabled: true,
    });

    services.current.httpMaterialFactory.setBakeColorTexture(BakeColorTextureMode.DONT_BAKE);

    return () => {
      services.current = undefined;
      renderer.dispose();
    };
  }, []);

  useEffect(() => {
    if (filter.trim() === "") {
      setFilteredPaletteKeys(exteriorPaletteLibraryAsync.ids);
      return;
    }
    const filteredKeys = exteriorPaletteLibraryAsync.ids.filter((key) => key.startsWith(filter));
    if (!filteredKeys.includes(currentPaletteKey) && filteredKeys.length > 0) {
      setCurrentPaletteKey(filteredKeys[0]);
    }
    setFilteredPaletteKeys(filteredKeys);
  }, [filter]);

  useEffect(() => {
    void (async () => {
      const palette = await getExteriorPalette(currentPaletteKey);
      if (palette) {
        setPaletteFileNameExample(paletteFileName(currentPaletteKey, palette));
      }
    })();
  }, [currentPaletteKey, filenameStyle, filenameIllegalCharsReplacement, fileFormat]);

  useEffect(() => {
    if (!services.current || !fileStorageBaseURL) {
      return;
    }
    void (async () => {
      const palette = await getExteriorPalette(currentPaletteKey);

      if (!services.current || !palette || !thumbnailImgRef.current) {
        return;
      }

      const { dataURL } = await genExteriorPaletteThumbnail(
        palette,
        services.current,
        fileFormat,
        { paletteLayout },
        fileStorageBaseURL,
        {
          noCache: !allowMaterialThumbnailCache,
          forceFlatColorFallback,
        },
      );
      thumbnailImgRef.current.src = dataURL;
    })();
  }, [
    services,
    fileFormat,
    currentPaletteKey,
    allowMaterialThumbnailCache,
    forceFlatColorFallback,
    paletteLayout,
    fileStorageBaseURL,
  ]);

  const filenameStyleOptions = Object.keys(FilenameStyle).map((filenameStyle) => {
    return { label: filenameStyle, value: FilenameStyle[filenameStyle] };
  });
  const downloadAllLabel = `Download ${filteredPaletteKeys.length} thumbnails (warning: slow!)`;

  return (
    <div>
      <Head>
        <title>{getPageTitle("Palette Thumbnail Test")}</title>
        <meta name="robots" content="noindex" />

        <style>{`
          .row, .col {  overflow: hidden; position: absolute; }
          .row { left: 0; right: 0; }
          .col { top: 0; bottom: 0; }
          .scroll-x { overflow-x: auto; }
          .scroll-y { overflow-y: auto; }

          .ui-container {
            width: 800px;
            padding: 10px;
          }

          input[type=button] {
              margin: 10px;
              padding: 10px;
          }

          select {
            color: black;
          }

          .dg.main {
            bottom: 0;
            overflow-x: hidden;
            overflow-y: auto;
          }

          .dg .property-name {
            width: 100px;
          }
        `}</style>
      </Head>

      <div className="ui-container">
        <p>
          Filter palette IDs by prefix:&nbsp;
          <input type="text" onChange={(e) => setFilter(e.target.value)} value={filter}></input>
        </p>
        <p>Number of palettes: {filteredPaletteKeys.length}</p>
        <Select
          value={currentPaletteKey}
          placeholder="Load palette"
          options={filteredPaletteKeys.map((paletteKey) => {
            return { label: paletteKey, value: paletteKey };
          })}
          onChange={(value) => {
            setCurrentPaletteKey(value as string);
          }}
        />
        <h2>Current: {currentPaletteKey}</h2>
        <img id="thumbnailPreview" ref={thumbnailImgRef} height="300" />
        <h2>Image settings</h2>
        <p>
          File format:{" "}
          <select
            value={fileFormat}
            onChange={(e) => {
              setFileFormat(e.currentTarget.value as "image/jpeg" | "image/png");
            }}
          >
            <option value="image/jpeg">JPEG</option>
            <option value="image/png">PNG</option>
          </select>
        </p>
        <p>
          <input
            type="checkbox"
            checked={allowMaterialThumbnailCache}
            onChange={(e) => {
              setAllowMaterialThumbnailCache(e.currentTarget.checked);
            }}
          />
          Allow material thumbnail caching (enabled in Showroom by default)
        </p>
        <p>
          <input
            type="checkbox"
            checked={forceFlatColorFallback}
            onChange={(e) => {
              setForceFlatColorFallback(e.currentTarget.checked);
            }}
          />
          Force flat color fallback for materials. This is used on mobile platforms in case the material thumbnail is
          not found from the AWS cache.
        </p>
        <p>
          <input
            type="checkbox"
            checked={paletteLayout === "rect"}
            onChange={(e) => {
              setPaletteLayout(e.currentTarget.checked ? "rect" : "square");
            }}
          />
          Use rect layout
        </p>
        <div>
          File naming:
          <Select
            value={filenameStyle}
            placeholder="Filename style"
            options={filenameStyleOptions}
            onChange={(value) => {
              setFilenameStyle(value as FilenameStyle);
            }}
          />
        </div>
        <div>
          Replace disallowed characters in filename with:
          <Select
            value={filenameIllegalCharsReplacement}
            placeholder="Filename style"
            options={filenameIllegalCharsReplacementOptions}
            onChange={(value) => {
              setFilenameIllegalCharsReplacement(value as string);
            }}
          />
        </div>
        <p>Example filename: {paletteFileNameExample}</p>
        <p>
          <input type="button" onClick={() => downloadCurrent()} value="Download thumbnail" />
          <input type="button" onClick={() => downloadAll()} value={downloadAllLabel} />
        </p>
      </div>
    </div>
  );
}

const PaletteThumbnailTestPage = (): ReactNode => <PaletteThumbnailTest />;

export const Route = createFileRoute("/debug/palette-thumbnail-test")({
  component: withAuthentication(PaletteThumbnailTestPage, { superAdmin: true }),
});
